import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { launchFlyToCart, nearestCardImage } from '@theme/product-form';

/**
 * R-TAP: what the drawer says while it is open on a promise. Functional status,
 * not brand copy - Charlie to reword if he wants it in EXALT's voice.
 */
const CART_PENDING_TEXT = 'Adding to your box';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * R-TAP (17 Sep 2026): open the drawer on the TAP, not on the response.
   *
   * Every add path used to `await cartController.add(...)` and only then call
   * open(), so the drawer appeared 685-1,514 ms after the button was pressed
   * and nothing at all happened in between. It now opens in the same task as
   * the tap and the line morphs in when the response lands.
   *
   * `data-cart-pending` marks that window. The drawer must not assert anything
   * about the cart while it is open on a promise - above all it must not say
   * "your cart is empty" on the very add that is about to fill it - so the CSS
   * in snippets/ctr-cart-drawer.liquid hides the empty-state body and dims the
   * content until the morph arrives, and the heading below says what is
   * happening instead. No skeleton rows: a fake line is a claim, and this does
   * not know what it is adding.
   *
   * R25 is preserved: Build-a-Box below 750 px keeps its inline box panel on
   * screen and must not get a drawer on top of it.
   *
   * R-TAP-ANIM, Codex round TAP-12 finding 1. **ONE DECISION POINT FOR EVERY ADD.**
   * `respectAutoOpen` started as an opt-in for product-form.js while the three
   * PDP buy boxes kept calling `open()` unconditionally, which meant Charlie's
   * instruction - "when it has stuff dont open" - held on the cards and not on a
   * PDP. It is the default now, every caller passes a `source`, and **when this
   * declines to open, IT flies the product photo to the cart icon** rather than
   * leaving each caller to remember. Pass `respectAutoOpen: false` only for a
   * surface that must always open.
   *
   * @param {{respectAutoOpen?: boolean, source?: Element | null}} [options]
   * @returns {boolean} whether the drawer is being opened for this add.
   */
  openForPendingAdd(options) {
    // R25: the inline box panel IS the feedback here, so no drawer and no flight.
    if (this.#isBuildABoxPanelVisible()) return false;

    if (options?.respectAutoOpen !== false && !this.#wouldAutoOpen()) {
      const source = options?.source ?? null;
      launchFlyToCart(source, nearestCardImage(source));
      return false;
    }

    // R-TAP, Codex round TAP-6 finding 3. COUNTED, NOT A BOOLEAN. Two adds can be
    // outstanding at once - cartController serialises the REQUESTS, but each
    // caller opens the drawer before awaiting, so both are in their pending
    // window together. With a single flag the second tap captured the already
    // substituted "Adding..." as the text to restore, and the first one home
    // cleared the shared mark while the other was still pending: a drawer stuck
    // saying "Adding to your basket..." for ever if both were refused, or the
    // empty state restored underneath an add still in flight.
    this.#pendingAdds += 1;
    this.dataset.cartPending = 'true';

    this.#syncPendingState();
    this.open();

    return true;
  }

  /**
   * Say what is happening in the drawer's own empty-state heading.
   *
   * On a FIRST add the drawer renders its empty state, and hiding only the "your
   * cart is empty" heading was not enough: the body underneath still offered
   * "Log in to check out faster" and "Continue shopping", which is the same claim
   * in different words with a button that shuts the drawer. The body is hidden by
   * CSS; this reuses the heading that is already there rather than adding a
   * skeleton or a second loading component.
   *
   * R-TAP, Codex round TAP-7 finding 1: RE-ASSERTED AFTER EVERY MORPH, not applied
   * once. Two adds from an empty cart are serialised by the controller, so if the
   * FIRST is refused its reconcile morphs a fresh server-rendered "Your cart is
   * empty" into the drawer - while the second add is still on its way.
   *
   * THE MORPH TAKES THE ATTRIBUTE TOO. `morphCartPortion` syncs this element's
   * attributes to the server's markup, which has no `data-cart-pending`, so the
   * mark is stripped whatever the counter says. Both halves are restored here.
   */
  #syncPendingState() {
    if (this.#pendingAdds === 0) return;

    this.dataset.cartPending = 'true';

    const heading = this.querySelector('.cart-drawer__heading--empty');
    if (!heading || heading.textContent === CART_PENDING_TEXT) return;

    // Whatever the server last rendered is what gets put back, so a morph that
    // replaced the node cannot leave a stale string behind.
    this.#emptyHeadingText = heading.textContent;
    heading.textContent = CART_PENDING_TEXT;
  }

  /**
   * Leaves the pending state without waiting for a cart update. The one path
   * that needs this is a refused subscription add: cartController.add() returns
   * without reconciling, so no cart update is coming to clear it.
   */
  clearPendingAdd() {
    if (this.#pendingAdds > 0) this.#pendingAdds -= 1;

    // Another add is still out. Leave the pending state exactly as it is.
    if (this.#pendingAdds > 0) return;

    delete this.dataset.cartPending;

    // Put the empty-state wording back. On a successful add the morph has
    // already replaced this node, so this only matters when the add FAILED and
    // the customer is left looking at the empty cart they started with.
    if (this.#emptyHeadingText === null) return;

    const heading = this.querySelector('.cart-drawer__heading--empty');
    if (heading) heading.textContent = this.#emptyHeadingText;
    this.#emptyHeadingText = null;
  }

  /** @type {string | null} The empty-state wording, held while it is replaced. */
  #emptyHeadingText = null;

  /** @type {number} How many adds are inside their pending window right now. */
  #pendingAdds = 0;

  /**
   * Whether #handleCartAdd would open the drawer for the next cart update. One
   * copy of that test, so the tap-time open and the response-time open cannot
   * disagree about when the drawer is supposed to appear.
   * @returns {boolean}
   */
  #wouldAutoOpen() {
    return this.hasAttribute('auto-open') && this.dataset.cartItemCount === '0';
  }

  /** @returns {boolean} true on Build-a-Box below 750px, where R25 says no drawer. */
  #isBuildABoxPanelVisible() {
    if (!this.dataset?.templateName?.includes('build-a-box')) return false;

    return !window.matchMedia('(min-width: 750px)').matches;
  }

  #handleCartAdd = () => {
    // R-TAP, round TAP-6 finding 3: this no longer calls clearPendingAdd(). It is
    // not paired with an openForPendingAdd(), so decrementing here would unbalance
    // the count - and every caller already clears in a `finally`, a few
    // milliseconds after this runs. The line below is a floor, not a mechanism:
    // it only fires if the count says nothing is outstanding and the mark somehow
    // survived anyway.
    if (this.#pendingAdds === 0) delete this.dataset.cartPending;

    // The morph that runs off THIS SAME EVENT may put a server-rendered empty
    // state back while another add is still out (round TAP-7 finding 1). Both
    // orders are covered: cart-items-component's morph listener may run before or
    // after this one depending on which element connected first, so the state is
    // re-asserted now AND again in a microtask, which is after every synchronous
    // listener for this event has finished.
    this.#syncPendingState();
    queueMicrotask(() => this.#syncPendingState());

    const isFirst = this.#wouldAutoOpen();

    // R-TOAST (6 Sep 2026): the non-first-add branch used to dispatch
    // cart-drawer-store:show-notification for a Toastify toast. The feature was
    // disabled and the library removed, so an add that is not the first item
    // now does nothing here - the drawer's own morph already reflects it.
    if (isFirst) {
      
      // R25: build-a-box was excluded outright because its inline box panel was
      // always on screen. That panel is desktop-hidden now, so on desktop the
      // drawer is the only cart and should auto-open like everywhere else.
      // Below 750px the panel is still there, so keep the drawer shut.
      // R-TAP: one copy of that test, shared with openForPendingAdd().
      if (!this.#isBuildABoxPanelVisible()) {
        this.showDialog();
      }
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
